import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { authApi } from '@/api/index';
import { isRedirectResponse } from '@/api/routes/auth/types';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { addSnack } from '@/store/slices/snackSlice';
import Button from '@/uikit/Button/Button';
import TextField from '@/uikit/inputs/TextField/TextField';
import { getCookie, setCookie } from '@/utils/cookie';

import FormContainer from './FormContainer/FormContainer';

import styles from './styles.module.scss';

const LoginSchema = z.object({
  email: z.string()
    .email(),
  password: z.string(),
});

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState<boolean>(false);
  const [isAutofillСomplete, setAutofillСomplete] = useState<boolean>(false);
  const [isOtpProcessing, setIsOtpProcessing] = useState<boolean>(false);

  const resolveNavigate = (url: string) => {
    navigate(url);
  };

  const handleOtpLogin = async (otp: string) => {
    setIsOtpProcessing(true);
    setLoading(true);
    try {
      const response = await authApi.login({ otp });
      if (response && response.status === 200) {
        const { data } = response;
        if (!isRedirectResponse(data)) {
          setCookie('jwt', data.jwt, { 'max-age': 3153600 });
          setCookie('rt', data.rt, { 'max-age': 3153600 });
          resolveNavigate('/admin');
        }
      }
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 401) {
        dispatch(addSnack({
          lngKey: 'pages.login.wrongCredentials',
          variant: 'error',
        }));
      } else {
        dispatch(addSnack({
          children: e instanceof AxiosError ? e?.response?.data?.error : e instanceof Error ? e.message : '',
          lngKey: 'snack.error.default',
          variant: 'error',
        }));
      }
    } finally {
      setSearchParams({}, { replace: true });
      setLoading(false);
      setIsOtpProcessing(false);
    }
  };

  const {
    errors,
    values,
    handleChange,
    handleSubmit,
    touched,
  } = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: toFormikValidationSchema(LoginSchema),
    onSubmit: async (formikValues) => {
      setLoading(true);
      try {
        const response = await authApi.login({
          login: formikValues.email,
          password: formikValues.password,
        });
        if (response && response.status === 200) {
          const { data } = response;

          if (isRedirectResponse(data)) {
            const redirectUrl = new URL(data.redirect_to);
            redirectUrl.searchParams.set('otp', data.otp);
            window.location.href = redirectUrl.toString();
            return;
          }

          setCookie('jwt', data.jwt, { 'max-age': 3153600 });
          setCookie('rt', data.rt, { 'max-age': 3153600 });
          resolveNavigate('/admin');
        }
      } catch (e) {
        if (e instanceof AxiosError && e.response?.status === 401) {
          dispatch(addSnack({
            lngKey: 'pages.login.wrongCredentials',
            variant: 'error',
          }));
          return;
        }
        dispatch(addSnack({
          children: e instanceof AxiosError ? e?.response?.data?.error : e instanceof Error ? e.message : '',
          lngKey: 'snack.error.default',
          variant: 'error',
        }));
      } finally {
        setLoading(false);
      }
    },
  });

  const handleAutoFill = () => {
    const fields = document.querySelectorAll('input:-webkit-autofill');
    fields.forEach(() => {
      setAutofillСomplete(true);
    });
  };

  useEffect(() => {
    const jwt = getCookie('jwt');
    const rt = getCookie('rt');
    const otp = searchParams.get('otp');

    if (otp) {
      handleOtpLogin(otp);
      return;
    }

    if (jwt || rt) resolveNavigate('/admin');

    const timeout = setTimeout(() => {
      handleAutoFill();
    }, 300);

    return () => clearTimeout(timeout);
  }, []);

  if (isOtpProcessing) {
    return (
      <FormContainer title={t('pages.login.otpAuthenticating')}>
        <Helmet title={t('pages.login.helmet')} />
        <div className={styles.form}>
          <Button
            loading
            disabled
            fullWidth
            lngKey="pages.login.otpAuthenticating"
          />
        </div>
      </FormContainer>
    );
  }

  return (
    <FormContainer title={t('pages.login.helmet')}>
      <Helmet
        title={t('pages.login.helmet')}
      />
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.field}>
          <TextField
            id="email"
            name="email"
            lngKey="labels.email"
            type="email"
            value={values.email}
            onChange={handleChange}
            fullWidth
            error={touched.email ? errors.email : ''}
            isAutofillСomplete={isAutofillСomplete}
          />
        </div>
        <div className={styles.field}>
          <TextField
            id="password"
            name="password"
            lngKey="labels.password"
            type="password"
            value={values.password}
            onChange={handleChange}
            fullWidth
            error={touched.password ? errors.password : ''}
            isAutofillСomplete={isAutofillСomplete}
          />
        </div>
        <Button
          type="submit"
          loading={loading}
          fullWidth
          lngKey="button.login"
        />
      </form>
    </FormContainer>
  );
};

export default Login;
